/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useCallback } from 'react';
import type { ConflictInfo, ConflictResolution } from '../../components/conflicts';
import type { ProductVelocitySettings } from '@shared/types/product-app-settings';
import type { RemoteVelocityStatus, SyncToMainAnalysis, SyncToMainDecision, SourceControlProvider } from '../../components/changes';
import { generateUniquePRTitle } from '../../components/changes';
import {
  syncVelocityBranch,
  syncVelocityBranchAuto,
  checkConflicts,
  batchResolveConflicts,
  getRemoteVelocityStatus,
  pushVelocityToTeam,
  pullVelocityFromTeam,
  createVelocityPR,
  getSyncToMainAnalysis,
  syncVelocityToMain,
} from '../../lib/api';

interface ActiveProduct {
  repositoryUrl?: string | null;
  repoPath?: string | null;
  title?: string;
}

interface GitLabConfig {
  accessToken?: string;
  instanceUrl?: string;
}

interface ToastFn {
  (opts: { title: string; description: string; variant?: 'destructive' }): void;
}

interface UseSyncOperationsParams {
  activeProduct: ActiveProduct | null;
  sourceControlProvider: SourceControlProvider;
  accessToken: string | undefined;
  velocitySettings: ProductVelocitySettings;
  toast: ToastFn;
  loadStatus: () => Promise<void>;
  loadCommitHistory: () => Promise<void>;
  gitlabConfig: GitLabConfig | null;
  defaultBranch: string;
  organizationId?: string;
}

export function useSyncOperations({
  activeProduct,
  sourceControlProvider,
  accessToken,
  velocitySettings,
  toast,
  loadStatus,
  loadCommitHistory,
  gitlabConfig,
  defaultBranch,
  organizationId,
}: UseSyncOperationsParams) {
  // Conflict state
  const [conflicts, setConflicts] = useState<ConflictInfo[]>([]);
  const [hasConflicts, setHasConflicts] = useState(false);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Remote velocity sync state
  const [remoteVelocityStatus, setRemoteVelocityStatus] = useState<RemoteVelocityStatus | null>(null);
  const [loadingRemoteStatus, setLoadingRemoteStatus] = useState(false);
  const [pushingToTeam, setPushingToTeam] = useState(false);
  const [pullingFromTeam, setPullingFromTeam] = useState(false);

  // Velocity PR state
  const [showVelocityPRDialog, setShowVelocityPRDialog] = useState(false);
  const [velocityPRTitle, setVelocityPRTitle] = useState('');
  const [velocityPRBody, setVelocityPRBody] = useState('');
  const [creatingVelocityPR, setCreatingVelocityPR] = useState(false);
  const [velocityPRError, setVelocityPRError] = useState<string | null>(null);

  // Velocity → Main sync state
  const [syncToMainAnalysis, setSyncToMainAnalysis] = useState<SyncToMainAnalysis | null>(null);
  const [syncToMainDecision, setSyncToMainDecision] = useState<SyncToMainDecision | null>(null);
  const [loadingSyncToMainAnalysis, setLoadingSyncToMainAnalysis] = useState(false);
  const [syncingToMain, setSyncingToMain] = useState(false);

  const checkForConflicts = useCallback(async () => {
    if (!activeProduct?.repositoryUrl) return;

    setCheckingConflicts(true);
    try {
      const result = await checkConflicts(activeProduct.repositoryUrl, sourceControlProvider);
      if (!result.success) {
        throw new Error(result.error || 'Failed to check for conflicts');
      }
      setHasConflicts(result.hasConflicts || false);

      if (result.conflicts && result.conflicts.length > 0) {
        setConflicts(result.conflicts.map((filePath: string) => ({
          filePath,
          entityId: filePath.split('/').pop()?.replace('.md', '') || 'unknown',
          entityType: filePath.includes('issues') ? 'issue' as const : 'user-story' as const,
          documentType: filePath.includes('issues') ? 'issues' as const : 'user-stories' as const,
          baseVersion: '',
          localVersion: '',
          remoteVersion: ''
        })));
      } else {
        setConflicts([]);
      }
    } catch (error: any) {
      console.error('Error checking for conflicts:', error);
    } finally {
      setCheckingConflicts(false);
    }
  }, [activeProduct, sourceControlProvider]);

  const handleSync = useCallback(async () => {
    if (!activeProduct?.repositoryUrl) return;

    setSyncing(true);
    try {
      if (velocitySettings.autoAIConflictResolution) {
        console.log('[Changes] Using auto-resolve sync with semantic rules + AI');

        const result = await syncVelocityBranchAuto(
          activeProduct.repositoryUrl,
          'merge',
          velocitySettings.aiResolutionThreshold ?? 0.8,
          true,
          sourceControlProvider
        );

        if (result.success) {
          setHasConflicts(false);
          setConflicts([]);

          if (result.autoResolved && result.resolutions && result.resolutions.length > 0) {
            console.log('[Changes] Auto-resolved conflicts:', result.resolutions);
            toast({
              title: 'Sync Complete',
              description: `${result.resolutions.length} conflict(s) automatically resolved`
            });
          } else {
            toast({
              title: 'Sync Complete',
              description: result.message || 'Successfully synced with main'
            });
          }

          await loadStatus();
          await loadCommitHistory();
          return;
        }

        if (result.action === 'conflicts' && result.conflicts && result.conflicts.length > 0) {
          setHasConflicts(true);

          if (result.autoResolveResult?.resolved?.length) {
            console.log('[Changes] Auto-resolved some conflicts:', result.autoResolveResult.resolved);
            toast({
              title: 'Partial Resolution',
              description: `${result.autoResolveResult.resolved.length} conflict(s) auto-resolved, ${result.conflicts.length} need manual review`
            });
          }

          const conflictDetails: ConflictInfo[] = result.conflicts.map((c) => ({
            filePath: c.filePath,
            entityId: c.filePath.split('/').pop()?.replace('.md', '') || 'unknown',
            entityType: c.filePath.includes('issues') ? 'issue' as const : 'user-story' as const,
            documentType: c.filePath.includes('issues') ? 'issues' as const : 'user-stories' as const,
            baseVersion: '',
            localVersion: '',
            remoteVersion: ''
          }));

          setConflicts(conflictDetails);
          setShowConflictDialog(true);
          toast({
            title: 'Conflicts with main',
            description: `${result.conflicts.length} files have conflicts — resolve before syncing`,
            variant: 'destructive'
          });
          return;
        }

        throw new Error(result.error || 'Sync failed');
      } else {
        const result = await syncVelocityBranch(
          activeProduct.repositoryUrl,
          'merge',
          sourceControlProvider
        );

        if (result.action === 'conflicts' && result.conflicts) {
          setHasConflicts(true);

          const conflictDetails: ConflictInfo[] = result.conflicts.map((c) => ({
            filePath: c.file,
            entityId: c.file.split('/').pop()?.replace('.md', '') || 'unknown',
            entityType: c.file.includes('issues') ? 'issue' as const : 'user-story' as const,
            documentType: c.file.includes('issues') ? 'issues' as const : 'user-stories' as const,
            baseVersion: '',
            localVersion: '',
            remoteVersion: ''
          }));

          setConflicts(conflictDetails);
          setShowConflictDialog(true);
          toast({
            title: 'Merge Conflicts',
            description: `${result.conflicts?.length || 0} file(s) have conflicts that need resolution`,
            variant: 'destructive'
          });
        } else if (!result.success) {
          throw new Error(result.error || 'Sync failed');
        } else {
          setHasConflicts(false);
          setConflicts([]);
          toast({
            title: 'Sync Complete',
            description: result.message || 'Successfully synced with main'
          });
          await loadStatus();
          await loadCommitHistory();
        }
      }
    } catch (error: any) {
      console.error('Error syncing:', error);
      toast({
        title: 'Sync Failed',
        description: error.message || 'Failed to sync with main',
        variant: 'destructive'
      });
    } finally {
      setSyncing(false);
    }
  }, [activeProduct, sourceControlProvider, toast, loadStatus, loadCommitHistory, velocitySettings]);

  const handleConflictsResolved = useCallback((resolutions: ConflictResolution[]) => {
    const allResolved = resolutions.every(r => r.status === 'resolved');
    if (allResolved) {
      setHasConflicts(false);
      setConflicts([]);
      toast({
        title: 'Conflicts Resolved',
        description: 'All merge conflicts have been resolved'
      });
      loadStatus();
      loadCommitHistory();
    }
    setShowConflictDialog(false);
  }, [toast, loadStatus, loadCommitHistory]);

  const loadRemoteVelocityStatus = useCallback(async () => {
    if (!activeProduct?.repositoryUrl) return;

    setLoadingRemoteStatus(true);
    try {
      const result = await getRemoteVelocityStatus(activeProduct.repositoryUrl, sourceControlProvider);
      if (!result.success) {
        console.log('[Changes] Could not get remote velocity status (may be local-only repo)');
        setRemoteVelocityStatus(null);
        return;
      }
      setRemoteVelocityStatus({
        hasRemoteVelocity: result.hasRemoteVelocity,
        aheadOfRemote: result.aheadOfRemote,
        behindRemote: result.behindRemote,
        remoteVelocityCommit: result.remoteVelocityCommit,
        localVelocityCommit: result.localVelocityCommit,
        needsPull: result.needsPull,
        needsPush: result.needsPush,
        hasRemoteConflicts: result.hasRemoteConflicts
      });
    } catch (error: any) {
      console.error('Error loading remote velocity status:', error);
      setRemoteVelocityStatus(null);
    } finally {
      setLoadingRemoteStatus(false);
    }
  }, [activeProduct, sourceControlProvider]);

  const handlePushToTeam = useCallback(async () => {
    if (!activeProduct?.repositoryUrl || !accessToken) {
      toast({
        title: 'Error',
        description: `${sourceControlProvider === 'github' ? 'GitHub' : 'GitLab'} access token required for push`,
        variant: 'destructive'
      });
      return;
    }

    setPushingToTeam(true);
    try {
      const result = await pushVelocityToTeam(
        activeProduct.repositoryUrl,
        accessToken,
        sourceControlProvider,
        sourceControlProvider === 'gitlab' ? gitlabConfig?.instanceUrl : undefined
      );

      if (!result.success) {
        if (result.error?.includes('Conflicts') || result.error?.includes('Pull changes')) {
          toast({
            title: 'Push Blocked',
            description: result.error || 'Conflicts detected with remote velocity. Pull changes first.',
            variant: 'destructive'
          });
          return;
        }
        throw new Error(result.error || 'Failed to push to team');
      }

      toast({
        title: 'Pushed to Team',
        description: result.message || `Successfully pushed ${result.pushedCommits || 0} commit(s) to team`
      });

      await loadRemoteVelocityStatus();
      await loadCommitHistory();
    } catch (error: any) {
      console.error('Error pushing to team:', error);
      toast({
        title: 'Push Failed',
        description: error.message || 'Failed to push velocity to team',
        variant: 'destructive'
      });
    } finally {
      setPushingToTeam(false);
    }
  }, [activeProduct, accessToken, sourceControlProvider, gitlabConfig, toast, loadRemoteVelocityStatus, loadCommitHistory]);

  const handlePullFromTeam = useCallback(async () => {
    if (!activeProduct?.repositoryUrl) return;

    setPullingFromTeam(true);
    try {
      const result = await pullVelocityFromTeam(activeProduct.repositoryUrl, sourceControlProvider);

      if (result.action === 'conflicts' && result.conflicts) {
        setHasConflicts(true);

        const conflictDetails: ConflictInfo[] = result.conflicts.map((c) => ({
          filePath: c.filePath || '',
          entityId: c.entityId || c.filePath?.split('/').pop()?.replace('.md', '') || 'unknown',
          entityType: c.entityType || (c.filePath?.includes('issues') ? 'issue' as const : 'user-story' as const),
          documentType: c.documentType || (c.filePath?.includes('issues') ? 'issues' as const : 'user-stories' as const),
          baseVersion: c.baseVersion || '',
          localVersion: c.localVersion || '',
          remoteVersion: c.remoteVersion || ''
        }));

        if (velocitySettings.autoAIConflictResolution) {
          console.log('[Changes] Auto AI conflict resolution enabled, attempting cascade...');
          toast({
            title: 'Resolving Conflicts',
            description: `Attempting AI resolution for ${conflictDetails.length} conflict(s)...`
          });

          try {
            const aiResult = await batchResolveConflicts(
              conflictDetails.map(c => ({
                filePath: c.filePath,
                entityId: c.entityId,
                entityType: c.entityType,
                documentType: c.documentType,
                baseVersion: c.baseVersion,
                localVersion: c.localVersion,
                remoteVersion: c.remoteVersion
              })),
              velocitySettings.aiResolutionThreshold ?? 0.8,
              sourceControlProvider
            );

            const autoApplied = aiResult.resolved?.filter(r => r.autoApplied) || [];
            const needsReview = aiResult.resolved?.filter(r => !r.autoApplied) || [];
            const failed = aiResult.failed || [];

            console.log(`[Changes] AI resolution result: ${autoApplied.length} auto-applied, ${needsReview.length} need review, ${failed.length} failed`);

            if (autoApplied.length > 0) {
              toast({
                title: 'AI Resolved Conflicts',
                description: `${autoApplied.length} conflict(s) resolved automatically with high confidence`
              });
            }

            if (needsReview.length === 0 && failed.length === 0) {
              setHasConflicts(false);
              setConflicts([]);
              toast({
                title: 'All Conflicts Resolved',
                description: 'AI successfully resolved all conflicts'
              });
              await loadRemoteVelocityStatus();
              await loadStatus();
              await loadCommitHistory();
              return;
            }

            const remainingConflicts = conflictDetails.filter(c => {
              const wasAutoApplied = autoApplied.some(a => a.entityId === c.entityId);
              return !wasAutoApplied;
            });

            if (remainingConflicts.length > 0) {
              setConflicts(remainingConflicts);
              setShowConflictDialog(true);
              toast({
                title: 'Manual Review Required',
                description: `${remainingConflicts.length} conflict(s) need manual resolution`
              });
            }
          } catch (aiError: any) {
            console.error('[Changes] AI resolution cascade failed:', aiError);
            setConflicts(conflictDetails);
            setShowConflictDialog(true);
            toast({
              title: 'AI Resolution Failed',
              description: 'Falling back to manual conflict resolution',
              variant: 'destructive'
            });
          }
        } else {
          setConflicts(conflictDetails);
          setShowConflictDialog(true);
          toast({
            title: 'Merge Conflicts',
            description: `${conflictDetails.length} file(s) have conflicts that need resolution`,
            variant: 'destructive'
          });
        }
        return;
      }

      if (!result.success) {
        throw new Error(result.error || 'Failed to pull from team');
      }

      toast({
        title: 'Pulled from Team',
        description: result.message || `Successfully pulled ${result.pulledCommits || 0} commit(s) from team`
      });

      await loadRemoteVelocityStatus();
      await loadStatus();
      await loadCommitHistory();
    } catch (error: any) {
      console.error('Error pulling from team:', error);
      toast({
        title: 'Pull Failed',
        description: error.message || 'Failed to pull velocity from team',
        variant: 'destructive'
      });
    } finally {
      setPullingFromTeam(false);
    }
  }, [activeProduct, sourceControlProvider, toast, loadRemoteVelocityStatus, loadStatus, loadCommitHistory, velocitySettings]);

  const handleCreateVelocityPR = useCallback(async () => {
    if (!activeProduct?.repositoryUrl || !accessToken || !velocityPRTitle.trim()) return;

    setCreatingVelocityPR(true);
    setVelocityPRError(null);

    try {
      const result = await createVelocityPR(
        activeProduct.repositoryUrl,
        accessToken,
        velocityPRTitle,
        velocityPRBody,
        sourceControlProvider,
        sourceControlProvider === 'gitlab' ? gitlabConfig?.instanceUrl : undefined,
        defaultBranch
      );

      if (!result.success) {
        const errorMessage = result.error || 'Failed to create PR';
        setVelocityPRError(errorMessage);
        if (errorMessage.toLowerCase().includes('already exists')) {
          const newTitle = generateUniquePRTitle(activeProduct.title, defaultBranch);
          console.log('[Changes] PR name conflict, suggesting new title:', newTitle);
        }
        return;
      }

      toast({
        title: sourceControlProvider === 'github' ? 'Pull Request Created' : 'Merge Request Created',
        description: `#${result.pr?.number || result.pr?.iid}: ${result.pr?.title}`
      });

      if (result.pr?.url || result.pr?.web_url) {
        window.open(result.pr.url || result.pr.web_url, '_blank');
      }

      setShowVelocityPRDialog(false);
      setVelocityPRTitle('');
      setVelocityPRBody('');
      setVelocityPRError(null);
    } catch (error: any) {
      console.error('Error creating velocity PR:', error);
      setVelocityPRError(error.message || 'Failed to create pull request');
    } finally {
      setCreatingVelocityPR(false);
    }
  }, [activeProduct, accessToken, velocityPRTitle, velocityPRBody, sourceControlProvider, gitlabConfig, defaultBranch, toast]);

  const loadSyncToMainAnalysis = useCallback(async () => {
    if (!activeProduct?.repositoryUrl) return;

    setLoadingSyncToMainAnalysis(true);
    try {
      const result = await getSyncToMainAnalysis(activeProduct.repositoryUrl, sourceControlProvider);
      if (!result.success) {
        console.error('Failed to load sync-to-main analysis:', result.error);
        setSyncToMainAnalysis(null);
        setSyncToMainDecision(null);
        return;
      }
      setSyncToMainAnalysis(result.analysis || null);
      setSyncToMainDecision(result.decision || null);
    } catch (error: any) {
      console.error('Error loading sync-to-main analysis:', error);
      setSyncToMainAnalysis(null);
      setSyncToMainDecision(null);
    } finally {
      setLoadingSyncToMainAnalysis(false);
    }
  }, [activeProduct, sourceControlProvider]);

  const handleSyncToMain = useCallback(async (forceAction?: 'auto-merge' | 'create-pr') => {
    if (!activeProduct?.repositoryUrl || !accessToken) return;

    setSyncingToMain(true);
    try {
      const result = await syncVelocityToMain(
        activeProduct.repositoryUrl,
        accessToken,
        sourceControlProvider,
        sourceControlProvider === 'gitlab' ? gitlabConfig?.instanceUrl : undefined,
        organizationId
      );

      if (!result.success) {
        if (result.conflicts && result.conflicts.length > 0) {
          toast({
            title: 'Sync Blocked',
            description: result.error || 'Conflicts detected - resolve before syncing',
            variant: 'destructive'
          });
          return;
        }
        throw new Error(result.error || `Failed to sync to ${defaultBranch}`);
      }

      if (result.action === 'merged') {
        toast({
          title: `Merged to ${defaultBranch}`,
          description: `Successfully merged ${result.syncedFiles?.length || 0} file(s) to ${defaultBranch} branch`
        });
      } else if (result.action === 'pr-created') {
        toast({
          title: sourceControlProvider === 'github' ? 'Pull Request Created' : 'Merge Request Created',
          description: `#${result.prNumber}: View in browser`
        });
        if (result.prUrl) {
          window.open(result.prUrl, '_blank');
        }
      } else if (result.action === 'skipped') {
        toast({
          title: 'No Changes',
          description: result.message || `velocity branch has no changes ahead of ${defaultBranch}`
        });
      }

      await loadSyncToMainAnalysis();
      await loadStatus();
    } catch (error: any) {
      console.error(`Error syncing to ${defaultBranch}:`, error);
      toast({
        title: 'Sync Failed',
        description: error.message || `Failed to sync velocity to ${defaultBranch}`,
        variant: 'destructive'
      });
    } finally {
      setSyncingToMain(false);
    }
  }, [activeProduct, accessToken, sourceControlProvider, gitlabConfig, toast, loadSyncToMainAnalysis, loadStatus]);

  return {
    // Conflict state
    conflicts,
    hasConflicts,
    showConflictDialog,
    setShowConflictDialog,
    checkingConflicts,
    syncing,
    setSyncing,
    checkForConflicts,
    handleSync,
    handleConflictsResolved,

    // Remote velocity
    remoteVelocityStatus,
    loadingRemoteStatus,
    pushingToTeam,
    pullingFromTeam,
    loadRemoteVelocityStatus,
    handlePushToTeam,
    handlePullFromTeam,

    // Velocity PR
    showVelocityPRDialog,
    setShowVelocityPRDialog,
    velocityPRTitle,
    setVelocityPRTitle,
    velocityPRBody,
    setVelocityPRBody,
    creatingVelocityPR,
    velocityPRError,
    setVelocityPRError,
    handleCreateVelocityPR,

    // Sync to main
    syncToMainAnalysis,
    syncToMainDecision,
    loadingSyncToMainAnalysis,
    syncingToMain,
    loadSyncToMainAnalysis,
    handleSyncToMain,
  };
}
